import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { auth } from "../firebase";

const FirebaseAuthContext = createContext(null);

export function FirebaseAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const unsubscribeRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const attach = (authInstance) => {
      if (!authInstance || typeof authInstance.onAuthStateChanged !== "function") {
        if (!cancelled) {
          setLoading(false);
        }
        return;
      }
      try {
        const unsub = authInstance.onAuthStateChanged((u) => {
          if (cancelled) return;
          setUser(u);
          setLoading(false);
        });
        unsubscribeRef.current = unsub;
      } catch (e) {
        if (!cancelled) setLoading(false);
      }
    };

    if (auth) {
      attach(auth);
    } else {
      let attempts = 0;
      const maxAttempts = 20;
      const interval = setInterval(() => {
        attempts++;
        if (auth) {
          clearInterval(interval);
          attach(auth);
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          if (!cancelled) setLoading(false);
        }
      }, 150);
    }

    return () => {
      cancelled = true;
      if (unsubscribeRef.current) {
        try {
          unsubscribeRef.current();
        } catch (e) {}
        unsubscribeRef.current = null;
      }
    };
  }, []);

  const login = async (email, password) => {
    const mod = await import("firebase/auth");
    if (!auth) throw new Error("Firebase is not configured.");
    const cred = await mod.signInWithEmailAndPassword(auth, email, password);
    return cred.user;
  };

  const logout = async () => {
    if (!auth) return;
    const mod = await import("firebase/auth");
    await mod.signOut(auth);
  };

  const isAuthenticated = Boolean(user);

  return (
    <FirebaseAuthContext.Provider
      value={{ user, loading, isAuthenticated, login, logout }}
    >
      {children}
    </FirebaseAuthContext.Provider>
  );
}

export function useFirebaseAuth() {
  const ctx = useContext(FirebaseAuthContext);
  if (!ctx) throw new Error("useFirebaseAuth must be used within FirebaseAuthProvider");
  return ctx;
}
